import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MyDocuments.css';

interface Document {
  id: string;
  name: string;
  filename: string;
  chunk_count: number;
  upload_date: string;
  tags: string[];
}

interface MyDocumentsProps {
  apiUrl: string;
  userToken: string | null;
  isOpen: boolean;
  onClose: () => void;
  onRefresh?: number;
}

const MyDocuments: React.FC<MyDocumentsProps> = ({ apiUrl, userToken, isOpen, onClose, onRefresh }) => {
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && userToken) {
      fetchDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userToken, onRefresh]);

  const fetchDocuments = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${apiUrl}/my-documents`, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });
      setDocuments(response.data.documents || []);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      await axios.delete(`${apiUrl}/my-documents/${docId}`, {
        headers: {
          'Authorization': `Bearer ${userToken}`
        }
      });
      setDocuments(docs => docs.filter(d => d.id !== docId));
      setDeleteConfirm(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete document');
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="docs-panel-overlay" onClick={onClose} />
      <div className={`docs-panel ${isOpen ? 'open' : ''}`}>
        <div className="docs-header">
          <h3>📚 My Documents</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {loading && <div className="docs-loading">Loading...</div>}

        {error && <div className="docs-error">{error}</div>}

        {!loading && documents.length === 0 && (
          <div className="docs-empty">
            <p>No documents uploaded yet</p>
            <small>Upload documents to build your knowledge base</small>
          </div>
        )}

        <div className="docs-list">
          {documents.map(doc => (
            <div key={doc.id} className="doc-card">
              <div className="doc-info">
                <div className="doc-name">{doc.name || doc.filename}</div>
                <div className="doc-meta">
                  {doc.chunk_count} chunks • {new Date(doc.upload_date).toLocaleDateString()}
                </div>
              </div>
              <button
                className="doc-delete-btn"
                onClick={() => setDeleteConfirm(doc.id)}
                title="Delete document"
              >
                🗑️
              </button>
            </div>
          ))}
        </div>

        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="confirm-modal">
            <div className="confirm-content">
              <p>Delete this document?</p>
              <div className="confirm-actions">
                <button
                  className="confirm-yes"
                  onClick={() => handleDelete(deleteConfirm)}
                >
                  Delete
                </button>
                <button
                  className="confirm-no"
                  onClick={() => setDeleteConfirm(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default MyDocuments;
